import { Box } from '@mui/material';
import { StageDialogCollapsePanel } from './StageDialogCollapsePanel';
import { validateSweepCriteria } from '../../../../../common/rule-builder/validateSweepCriteria';
import _isEqual from 'lodash/isEqual';
import useSingleGatePanel from '../../gates/useSingleGatePanel';
import { GatePanel } from '../../gates/GatePanel';
import { FunnelType } from '../../../../../../types/enums/FunnelType';
import { uniqueId } from '../../../../../../lib/uniqueId';
import { EnrichedFunnelLink } from '../../../funnel-links/types';
import { useMemo } from 'react';

interface RuleBuilderCollapsePanelProps {
  stageObjectName: string;
  exitCriteria: SweepExitCriteria;
  toStageName: string;
  readonly?: boolean;
  defaultIsCollapsed?: boolean;
  fromStageName: string;
  crmOrgId: string;
  onGateSave: (updatedExitCriteria: SweepExitCriteria) => void;
  onGateDelete: () => void;
  setGateDirtyState: (isDirty: boolean) => void;
  pluginType?: PluginTypes;
  funnelId: string;
  funnelName: string;
}

const RuleBuilderCollapsePanel = ({
  stageObjectName,
  exitCriteria,
  toStageName,
  readonly,
  defaultIsCollapsed,
  fromStageName,
  crmOrgId,
  onGateSave,
  onGateDelete,
  setGateDirtyState,
  pluginType,
  funnelId,
  funnelName,
}: RuleBuilderCollapsePanelProps) => {
  const {
    localState: _exitCriteria,
    onChangeAutoMove,
    gateRef,
    onChangeCondition,
    discardChanges,
    saveChanges,
  } = useSingleGatePanel({
    onGateSave,
    setGateDirtyState,
    exitCriteria,
  });

  const criteriaLength = _exitCriteria.criteria.length;
  const hasNoCriteria = _exitCriteria.criteria.length === 0;
  const hasErrors = validateSweepCriteria(_exitCriteria.criteria).length > 0;
  const isChanged = !_isEqual(_exitCriteria, exitCriteria);

  const enrichedFunnelLink: EnrichedFunnelLink = useMemo(() => {
    return {
      _id: uniqueId(),
      _type: FunnelType.SALESFORCE,
      funnelId,
      objectName: stageObjectName,
      funnelName,
      stageName: toStageName,
    };
  }, [funnelId, funnelName, stageObjectName, toStageName]);

  return (
    <StageDialogCollapsePanel
      defaultIsCollapsed={defaultIsCollapsed || hasErrors}
      from={fromStageName}
      totalConditions={criteriaLength}
      pluginType={pluginType}
      funnelId={funnelId}
      enrichedFunnelLink={enrichedFunnelLink}
    >
      <Box sx={{ padding: '0px' }}>
        <GatePanel
          exitCriteria={_exitCriteria}
          onChange={onChangeCondition}
          saveChanges={saveChanges}
          discardChanges={discardChanges}
          stageObjectName={stageObjectName}
          readonly={readonly}
          crmOrgId={crmOrgId}
          onGateDelete={onGateDelete}
          isChanged={isChanged}
          hasNoCriteria={hasNoCriteria}
          onChangeAutoMove={onChangeAutoMove}
          gateRef={gateRef}
          funnelName={funnelName}
        />
      </Box>
    </StageDialogCollapsePanel>
  );
};

export { RuleBuilderCollapsePanel };
